import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ExportOptionsHost } from "@/components/ExportOptionsHost";
import CookieConsent from "@/components/CookieConsent";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Layout from "@/components/Layout";
import ScoutingGate from "@/components/ScoutingGate";
import MedicalGate from "@/components/MedicalGate";
import PlanGate from "@/components/PlanGate";
import DirectorGate from "@/components/DirectorGate";
import AdminGate from "@/components/AdminGate";
import AnalysisGate from "@/components/AnalysisGate";

// Route pages are code-split so the first paint ships a small bundle; heavy deps
// (jsPDF, recharts, maps, xlsx) load only when their page is actually opened.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PlayersPage = lazy(() => import("@/pages/PlayersPage"));
const ToSignPage = lazy(() => import("@/pages/ToSignPage"));
const BalancePage = lazy(() => import("@/pages/BalancePage"));
const LeaguesPage = lazy(() => import("@/pages/LeaguesPage"));
const CompetitionsPage = lazy(() => import("@/pages/CompetitionsPage"));
const SquadPage = lazy(() => import("@/pages/SquadPage"));
const StartingXIPage = lazy(() => import("@/pages/StartingXIPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const SquadComparePage = lazy(() => import("@/pages/SquadComparePage"));
const MatchAnalysisPage = lazy(() => import("@/pages/MatchAnalysisPage"));
const TeamAnalysisPage = lazy(() => import("@/pages/TeamAnalysisPage"));
const OpponentPage = lazy(() => import("@/pages/OpponentPage"));
const TacticalBoardPage = lazy(() => import("@/pages/TacticalBoardPage"));
const RosterPlayerPage = lazy(() => import("@/pages/RosterPlayerPage"));
const SeasonCalendarPage = lazy(() => import("@/pages/SeasonCalendarPage"));
const ViscidiMatrixPage = lazy(() => import("@/pages/ViscidiMatrixPage"));
const IndividualTrainingPage = lazy(() => import("@/pages/IndividualTrainingPage"));
const PublicPlayerPage = lazy(() => import("@/pages/PublicPlayerPage"));
const PublicMedicalPage = lazy(() => import("@/pages/PublicMedicalPage"));
const PublicScoutPage = lazy(() => import("@/pages/PublicScoutPage"));
const MatchReportPage = lazy(() => import("@/pages/MatchReportPage"));
const PlayersToSeePage = lazy(() => import("@/pages/PlayersToSeePage"));
const PlayerDetail = lazy(() => import("@/pages/PlayerDetail"));
const DirectorPage = lazy(() => import("@/pages/DirectorPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const MedicalHealthPage = lazy(() => import("@/pages/MedicalHealthPage"));
const MedicalPlayerPage = lazy(() => import("@/pages/MedicalPlayerPage"));
const WellnessPage = lazy(() => import("@/pages/WellnessPage"));
const WellnessPlayerPage = lazy(() => import("@/pages/WellnessPlayerPage"));
const WellnessSurveyPage = lazy(() => import("@/pages/WellnessSurveyPage"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsersPage"));
const SupportInboxPage = lazy(() => import("@/pages/SupportInboxPage"));
const SuperAdminPage = lazy(() => import("@/pages/SuperAdminPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
      Loading…
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
      <LanguageProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <ExportOptionsHost />
        <BrowserRouter>
          <CookieConsent />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment/success" element={<PaymentResult status="success" />} />
            <Route path="/payment/cancel" element={<PaymentResult status="cancel" />} />
            <Route path="/share/:token" element={<PublicPlayerPage />} />
            <Route path="/medical-report/:token" element={<PublicMedicalPage />} />
            <Route path="/wellness/:token" element={<WellnessSurveyPage />} />
            <Route path="/areamedica/wellnesserpe/:handle" element={<WellnessSurveyPage />} />
            <Route path="/scout/:token" element={<PublicScoutPage />} />
            <Route path="/terms" element={<LegalPage doc="terms" />} />
            <Route path="/privacy" element={<LegalPage doc="privacy" />} />
            <Route path="/cookie" element={<LegalPage doc="cookie" />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/impostazioni" element={<SettingsPage />} />
              <Route element={<DirectorGate />}>
                <Route element={<PlanGate feature="director" />}>
                  <Route path="/direzione-sportiva" element={<DirectorPage />} />
                </Route>
              </Route>
              <Route element={<ScoutingGate />}>
                <Route path="/da-vedere" element={<PlayersToSeePage />} />
                <Route path="/calciatrici" element={<PlayersPage />} />
                <Route element={<PlanGate feature="players_to_sign" />}>
                  <Route path="/da-ingaggiare" element={<ToSignPage />} />
                </Route>
                <Route path="/paesi" element={<Navigate to="/campionati" replace />} />
                <Route path="/campionati" element={<LeaguesPage />} />
                <Route element={<PlanGate feature="squad" />}>
                  <Route path="/squad" element={<SquadPage />} />
                  <Route path="/bilancio" element={<BalancePage />} />
                </Route>
                <Route path="/confronto" element={<ComparePage />} />
                <Route path="/confronto-rosa" element={<SquadComparePage />} />
                </Route>
              <Route element={<AnalysisGate />}>
                <Route path="/analysis/match" element={<MatchAnalysisPage />} />
                <Route path="/analysis/team" element={<TeamAnalysisPage />} />
                <Route path="/competizioni" element={<CompetitionsPage />} />
                <Route path="/analysis/opponent" element={<OpponentPage />} />
                <Route path="/analysis/starting-xi" element={<StartingXIPage />} />
                <Route element={<PlanGate feature="tactical_board" />}>
                  <Route path="/analysis/board" element={<TacticalBoardPage />} />
                </Route>
                <Route path="/analysis/match/:handle" element={<RosterPlayerPage />} />
                <Route path="/allenatoreeanalisi/analisiplayer/:handle" element={<RosterPlayerPage />} />
                <Route path="/analysis/calendar" element={<SeasonCalendarPage />} />
                <Route path="/analysis/viscidi" element={<ViscidiMatrixPage />} />
                <Route path="/analysis/reports/:id" element={<MatchReportPage />} />
                <Route path="/analysis/training" element={<IndividualTrainingPage kind="all" />} />
                <Route path="/analysis/video" element={<IndividualTrainingPage kind="video" />} />
              </Route>
              <Route path="/calciatrici/:id" element={<PlayerDetail />} />
              <Route element={<PlanGate feature="medical" />}>
                <Route element={<MedicalGate />}>
                  <Route path="/analysis/medical" element={<MedicalHealthPage />} />
                  <Route path="/analysis/medical/:id" element={<MedicalPlayerPage />} />
                  <Route path="/analysis/wellness" element={<WellnessPage />} />
                  <Route path="/areamedica/:handle" element={<WellnessPlayerPage />} />
                </Route>
              </Route>
              <Route element={<AdminGate />}>
                <Route path="/admin/users" element={<AdminUsersPage />} />
              </Route>
              <Route path="/superadmin" element={<SuperAdminPage />} />
              <Route path="/admin/support" element={<SupportInboxPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
